/**
 * Add-ons and free delivery on the WEBSITE (Petal_Some Ug., 14 Oct 2026:
 * "We added chocolates as add ons but it does not reflect in the website…
 * is there a way… we are making a free delivery for website clients").
 *
 * ADD-ONS. A product's "Comes with / add-ons" list (`products.hireKit`, the
 * till's rules in lib/config/addOns.ts) goes online too:
 *   • a FREE piece always goes with the item — the product page says
 *     "Comes with …" and the order notes it under the item;
 *   • a PAID piece goes only when the shopper ticks it on the product page,
 *     with the count they chose for EACH item (the shop's count by default);
 *   • the order is written the way the till writes a sale (addOnLinesFor /
 *     addOnNoteFor): a charged or linked piece is its own line "+ Name", a
 *     free unlinked piece is the note "with N × Name" on the item.
 * The server prices every piece from the product and refuses a piece the
 * product does not have — the bag is never trusted for a price.
 *
 * A piece linked to a product the shop also sells alone moves that product's
 * stock, so it needs stock like any item: a paid one that has run out cannot
 * be chosen (the order is refused if it is short), a free one that has run
 * out is left off the order and the item says "not included".
 *
 * FREE DELIVERY. A product can be "Free delivery on the website". A delivery
 * is free only when EVERY item in the bag has it — one item without it and
 * the usual fee applies. Pickup is untouched.
 */

import { addOnLinesFor, addOnNoteFor, type AddOnLine, type AddOnPiece } from "./addOns";

/** A `products.hireKit` piece as stored. */
export interface StoredAddOnPiece {
  id: string;
  name: string;
  /** How many go with EACH item. */
  qty: number;
  /** UGX each on a sale; absent = free. */
  price?: number;
  /** A product the shop also sells alone: its stock moves. */
  productId?: string;
  /** Hire only: UGX per day. Never charged on a sale. */
  dailyRate?: number;
}

/** What the shopper chose for one bag line: a paid piece and how many with each item. */
export interface AddOnChoice {
  id: string;
  qty: number;
}

/** A paid piece chosen for a bag line, priced when it was chosen. */
export interface ChosenAddOn {
  id: string;
  name: string;
  /** How many with EACH item. */
  qty: number;
  /** UGX each. */
  price: number;
}

/** A piece as the website shows it — public fields only (never a cost). */
export interface WebsiteAddOn {
  id: string;
  name: string;
  /** The shop's count with each item (the default when a paid piece is ticked). */
  qty: number;
  /** UGX each; 0 = free. */
  price: number;
  /** Linked to a product the shop also sells alone. */
  productId?: string;
}

/** The most of one paid piece a shopper may take with each item. */
export const MAX_ADD_ON_QTY = 99;

/** Said wherever a bag mixes items with and without free delivery. */
export const FREE_DELIVERY_RULE = "Free delivery applies when every item in your bag has it.";

const num = (x: unknown): number => {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const lineCount = (qty: unknown): number => Math.max(1, Math.round(Number(qty) || 1));

/** The pieces the website may show: named, counted, one per id, prices rounded. */
export function websiteAddOns(pieces: ReadonlyArray<Partial<StoredAddOnPiece> | null | undefined> | null | undefined): WebsiteAddOn[] {
  const out: WebsiteAddOn[] = [];
  const seen = new Set<string>();
  for (const p of pieces || []) {
    if (!p) continue;
    const id = String(p.id ?? "").trim();
    const name = String(p.name ?? "").trim();
    const qty = Math.round(Number(p.qty) || 0);
    if (!id || !name || qty < 1 || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name,
      qty,
      price: Math.round(num(p.price)),
      ...(p.productId ? { productId: String(p.productId) } : {}),
    });
  }
  return out;
}

/** A piece the shopper pays for (and so chooses). */
export function isPaidAddOn(p: { price?: number | null } | null | undefined): boolean {
  return num(p?.price) > 0;
}

/** A count from a stepper: 1…99, else the fallback. */
export function clampAddOnQty(n: unknown, fallback = 1): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v) || v < 1) return Math.min(MAX_ADD_ON_QTY, Math.max(1, Math.round(fallback) || 1));
  return Math.min(MAX_ADD_ON_QTY, v);
}

export type ChoiceResult = { ok: true; paid: Array<ChosenAddOn & { productId?: string }> } | { ok: false; unknownId: string };

/**
 * The paid pieces a shopper chose, priced from the product, in the shop's
 * order. A free piece in the choice is ignored (it goes anyway, at the shop's
 * count); a count under 1 means "not taking it"; an id the product does not
 * have refuses the whole choice.
 */
export function resolveChoices(
  pieces: ReadonlyArray<Partial<StoredAddOnPiece> | null | undefined> | null | undefined,
  chosen: ReadonlyArray<Partial<AddOnChoice> | null | undefined> | null | undefined,
): ChoiceResult {
  const offered = websiteAddOns(pieces);
  const byId = new Map(offered.map((p) => [p.id, p]));
  const picked = new Map<string, number>();
  for (const c of chosen || []) {
    if (!c) continue;
    const id = String(c.id ?? "").trim();
    const piece = byId.get(id);
    if (!piece) return { ok: false, unknownId: id };
    if (!isPaidAddOn(piece)) continue;
    const qty = Math.round(Number(c.qty) || 0);
    if (qty < 1) {
      picked.delete(id);
      continue;
    }
    picked.set(id, Math.min(MAX_ADD_ON_QTY, qty));
  }
  return {
    ok: true,
    paid: offered
      .filter((p) => picked.has(p.id))
      .map((p) => ({
        id: p.id,
        name: p.name,
        qty: picked.get(p.id) as number,
        price: p.price,
        ...(p.productId ? { productId: p.productId } : {}),
      })),
  };
}

/** A piece going with an online line, in the till's shape (lib/config/addOns.ts). */
export type OnlinePiece = AddOnPiece & { id: string };

/** Every free piece at the shop's count, then each paid piece chosen at its chosen count — in the shop's order. */
export function piecesForOnlineLine(
  pieces: ReadonlyArray<Partial<StoredAddOnPiece> | null | undefined> | null | undefined,
  chosen: ReadonlyArray<Partial<AddOnChoice> | null | undefined> | null | undefined,
): { ok: true; pieces: OnlinePiece[] } | { ok: false; unknownId: string } {
  const r = resolveChoices(pieces, chosen);
  if (!r.ok) return r;
  const paidById = new Map(r.paid.map((p) => [p.id, p]));
  const out: OnlinePiece[] = [];
  for (const p of websiteAddOns(pieces)) {
    const link = p.productId ? { productId: p.productId } : {};
    if (!isPaidAddOn(p)) {
      out.push({ id: p.id, name: p.name, qtyOut: p.qty, ...link });
      continue;
    }
    const c = paidById.get(p.id);
    if (c) out.push({ id: p.id, name: p.name, qtyOut: c.qty, price: p.price, ...link });
  }
  return { ok: true, pieces: out };
}

/** One line of an online order, as the add-on planner needs it. */
export interface OnlineLineIn {
  /** The product's name, for the messages. */
  name: string;
  quantity: number;
  productId?: string | null;
  /** The product's pieces (`products.hireKit`); absent = none. */
  pieces?: ReadonlyArray<Partial<StoredAddOnPiece> | null | undefined> | null;
  /** The paid pieces the shopper chose. */
  chosen?: ReadonlyArray<Partial<AddOnChoice> | null | undefined> | null;
}

/** What one line takes with it. */
export interface OnlineLinePlan {
  /** "with 3 × TEBLERONE" — the free unlinked pieces, noted under the item ("" = none). */
  note: string;
  /** "1 × Good Girl perfume (out of stock)" — free linked pieces that could not go ("" = none). */
  notIncluded: string;
  /** Its own order lines, in the shop's order: linked → a product line, charged → a typed line. */
  lines: AddOnLine[];
  /** What those lines add to the order. */
  extra: number;
}

export type OnlinePlan = { ok: true; plans: OnlineLinePlan[]; extra: number } | { ok: false; error: string };

/**
 * The add-ons of a whole online order. `stockOnHand` gives the units on the
 * shelf of each LINKED product that runs out (absent = no limit — made to
 * order, pre-order); the order's own lines of that product are served first.
 * A paid piece the shelf cannot cover refuses the order with a sentence for
 * the shopper; a free one is left off and said so on the item.
 */
export function planOnlineAddOns(
  lines: ReadonlyArray<OnlineLineIn>,
  stockOnHand: Readonly<Record<string, number>> = {},
): OnlinePlan {
  const left = new Map<string, number>();
  for (const [pid, qty] of Object.entries(stockOnHand)) left.set(pid, Math.max(0, Math.floor(Number(qty) || 0)));
  // The order's own lines of a linked product take their share first.
  for (const l of lines) {
    const pid = l.productId ? String(l.productId) : "";
    if (pid && left.has(pid)) left.set(pid, (left.get(pid) as number) - lineCount(l.quantity));
  }

  const plans: OnlineLinePlan[] = [];
  let extra = 0;
  for (const l of lines) {
    const q = lineCount(l.quantity);
    const resolved = piecesForOnlineLine(l.pieces, l.chosen);
    if (!resolved.ok) {
      return {
        ok: false,
        error: `${l.name}: an extra you picked is no longer offered. Remove the item from your bag and add it again.`,
      };
    }
    const going: OnlinePiece[] = [];
    const missed: string[] = [];
    for (const p of resolved.pieces) {
      const pid = p.productId ? String(p.productId) : "";
      if (pid && left.has(pid)) {
        const need = p.qtyOut * q;
        const have = left.get(pid) as number;
        if (have < need) {
          if (isPaidAddOn(p)) {
            return {
              ok: false,
              error:
                have > 0
                  ? `Only ${have} × ${p.name} left — not enough to go with ${l.name}. Choose fewer and try again.`
                  : `${p.name} has run out, so it can't go with ${l.name}. Remove it and try again.`,
            };
          }
          missed.push(`${need} × ${p.name}`);
          continue;
        }
        left.set(pid, have - need);
      }
      going.push(p);
    }
    const addOnLines = addOnLinesFor(going, q);
    const lineExtra = addOnLines.reduce((s, a) => s + a.price * a.quantity, 0);
    extra += lineExtra;
    plans.push({
      note: addOnNoteFor(going, q),
      notIncluded: missed.length ? `${missed.join(", ")} (out of stock)` : "",
      lines: addOnLines,
      extra: lineExtra,
    });
  }
  return { ok: true, plans, extra };
}

// ─── Free delivery ──────────────────────────────────────────────────────────

/** Free delivery for the bag: it has items, and EVERY one has free delivery. */
export function freeDeliveryApplies(flags: ReadonlyArray<boolean | null | undefined>): boolean {
  return flags.length > 0 && flags.every((f) => f === true);
}

/** "all": delivery is free · "some": the bag mixes (say the rule) · "none". */
export function freeDeliveryState(flags: ReadonlyArray<boolean | null | undefined>): "all" | "some" | "none" {
  const n = flags.filter((f) => f === true).length;
  if (n === 0) return "none";
  return n === flags.length ? "all" : "some";
}

/** The fee a website order pays: 0 for a delivery whose every item has free delivery; pickup and anything else untouched. */
export function deliveryFeeWithFreeDelivery(
  method: string | null | undefined,
  fee: number,
  flags: ReadonlyArray<boolean | null | undefined>,
): number {
  const amount = Math.max(0, Number(fee) || 0);
  return method === "delivery" && freeDeliveryApplies(flags) ? 0 : amount;
}

// ─── The bag ────────────────────────────────────────────────────────────────

/** What the paid add-ons of one bag line add, for `lineQty` items. */
export function bagLineExtra(paid: ReadonlyArray<{ qty: number; price: number }> | null | undefined, lineQty: number): number {
  const q = lineCount(lineQty);
  return (paid || []).reduce((s, p) => s + Math.round(num(p.price)) * Math.max(0, Math.round(Number(p.qty) || 0)) * q, 0);
}

/**
 * One bag line's add-ons to show: the free pieces (from the product) and the
 * paid ones chosen. With the product's live list, a chosen piece the shop has
 * since removed (or made free) drops out and the price is today's; without
 * it (still loading) the prices saved in the bag stand.
 */
export function bagLineAddOns(
  chosen: ReadonlyArray<ChosenAddOn> | null | undefined,
  live: ReadonlyArray<WebsiteAddOn> | null | undefined,
): { free: WebsiteAddOn[]; paid: ChosenAddOn[] } {
  if (!live) {
    return {
      free: [],
      paid: (chosen || []).filter((c) => c && num(c.price) > 0 && Math.round(Number(c.qty) || 0) >= 1),
    };
  }
  const byId = new Map(live.map((p) => [p.id, p]));
  const paid: ChosenAddOn[] = [];
  for (const c of chosen || []) {
    const piece = c ? byId.get(String(c.id)) : undefined;
    if (!piece || !isPaidAddOn(piece)) continue;
    const qty = Math.round(Number(c.qty) || 0);
    if (qty < 1) continue;
    paid.push({ id: piece.id, name: piece.name, qty: Math.min(MAX_ADD_ON_QTY, qty), price: piece.price });
  }
  return { free: live.filter((p) => !isPaidAddOn(p)), paid };
}

/** The live terms of a product in the bag (products/shop:getBagTerms). */
export interface BagTerms {
  freeDelivery: boolean;
  addOns: WebsiteAddOn[];
}

export interface BagItemIn {
  productId: string;
  /** UGX each, as the bag holds it. */
  price: number;
  quantity: number;
  addOns?: ReadonlyArray<ChosenAddOn> | null;
}

export interface BagLineSummary {
  free: WebsiteAddOn[];
  /** Free linked pieces whose product has run out — they won't go with the order. */
  notIncluded: WebsiteAddOn[];
  paid: ChosenAddOn[];
  /** What the paid add-ons add to this line. */
  extra: number;
  freeDelivery: boolean;
}

export interface BagSummary {
  lines: BagLineSummary[];
  itemsTotal: number;
  addOnsTotal: number;
  subtotal: number;
  freeDelivery: "all" | "some" | "none";
  /** The delivery fee after free delivery (pickup: as given). */
  deliveryFee: number;
}

/**
 * The bag's money, the same way everywhere it is shown and sent. `shelf`
 * (linked product id → units, null = no limit) sets aside a free linked piece
 * that has run out, as the order will (planOnlineAddOns).
 */
export function bagSummary(
  items: ReadonlyArray<BagItemIn>,
  terms: ReadonlyMap<string, BagTerms> | null | undefined,
  delivery: { method: string; fee: number },
  shelf?: ReadonlyMap<string, number | null> | null,
): BagSummary {
  const lines: BagLineSummary[] = items.map((it) => {
    // A product the terms do not cover (another shop's, no longer online)
    // keeps what the bag saved; the order prices it from the product anyway.
    const t = terms?.get(String(it.productId));
    const { free: allFree, paid } = bagLineAddOns(it.addOns, t ? t.addOns : undefined);
    const q = lineCount(it.quantity);
    const short = (p: WebsiteAddOn) => {
      const units = p.productId ? shelf?.get(String(p.productId)) : undefined;
      return typeof units === "number" && units < p.qty * q;
    };
    return {
      free: allFree.filter((p) => !short(p)),
      notIncluded: allFree.filter(short),
      paid,
      extra: bagLineExtra(paid, it.quantity),
      freeDelivery: t?.freeDelivery === true,
    };
  });
  const itemsTotal = items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
  const addOnsTotal = lines.reduce((s, l) => s + l.extra, 0);
  const flags = lines.map((l) => l.freeDelivery);
  return {
    lines,
    itemsTotal,
    addOnsTotal,
    subtotal: itemsTotal + addOnsTotal,
    freeDelivery: freeDeliveryState(flags),
    deliveryFee: deliveryFeeWithFreeDelivery(delivery.method, delivery.fee, flags),
  };
}

/** The choices a bag line sends with its order: the paid pieces and their counts. */
export function choicesFor(paid: ReadonlyArray<ChosenAddOn> | null | undefined): AddOnChoice[] {
  return (paid || []).map((p) => ({ id: p.id, qty: p.qty }));
}
